import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { EmailPreferences } from "../components/EmailPreferences";
import { toast } from "sonner";
import { User, Crown, CreditCard, Pencil, Check, LogOut, ExternalLink, KeyRound, Camera, Sun, Moon, PauseCircle } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import api from "../lib/api";
import { ReferralCard } from "../components/ReferralCard";
import { MonthlyPrice } from "../lib/pricing";
import { InterviewUsage } from "../components/InterviewUsage";
import { CancelPremiumDialog } from "../components/CancelPremiumDialog";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";

const COMPARE = [
  ["Study questions", "50 a day per track", "Unlimited"],
  ["Mock Oral Boards", "1 free · summary report", "15 a month · full graded report"],
  ["Department-focused boards", null, true],
  ["Hands-free voice panel", null, true],
  ["Resume PDF & AI review", null, true],
  ["Department Research library", null, true],
];

function FreeVsPremium() {
  const cell = (v, pro) => v === true ? <Check className={`w-4 h-4 mx-auto ${pro ? "text-amber-500" : "text-slate-400"}`} /> : v === null ? <span className="text-slate-300">—</span> : v;
  return (
    <div className="mt-4 rounded-xl border border-slate-200 overflow-hidden text-xs" data-testid="free-vs-premium">
      <div className="grid grid-cols-[1.2fr_1fr_1fr] bg-slate-50 border-b border-slate-200 px-3 py-1.5 font-bold uppercase tracking-wider text-[10px] text-slate-500">
        <span /><span className="text-center">Free</span><span className="text-center text-amber-600 inline-flex items-center justify-center gap-1"><Crown className="w-3 h-3" /> Premium</span>
      </div>
      {COMPARE.map(([label, free, pro]) => (
        <div key={label} className="grid grid-cols-[1.2fr_1fr_1fr] items-center px-3 py-1.5 border-b border-slate-100 last:border-0" data-testid={`compare-row-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
          <span className="text-slate-600">{label}</span>
          <span className="text-center text-slate-700">{cell(free, false)}</span>
          <span className="text-center font-semibold text-slate-900">{cell(pro, true)}</span>
        </div>
      ))}
    </div>
  );
}



export default function Account() {
  const { user, logout, refresh } = useAuth();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [pwError, setPwError] = useState("");

  const uploadAvatar = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const img = new Image();
    img.onload = async () => {
      const size = 192, c = document.createElement("canvas");
      c.width = size; c.height = size;
      const s = Math.min(img.width, img.height);
      c.getContext("2d").drawImage(img, (img.width - s) / 2, (img.height - s) / 2, s, s, 0, 0, size, size);
      const dataUrl = c.toDataURL("image/jpeg", 0.85);
      try { await api.put("/account/avatar", { image_data_url: dataUrl }); toast.success("Photo updated."); refresh?.(); }
      catch (err) { toast.error(err.response?.data?.detail || "Could not upload photo."); }
      URL.revokeObjectURL(img.src);
    };
    img.onerror = () => toast.error("That file isn't a readable image.");
    img.src = URL.createObjectURL(file);
    e.target.value = "";
  };
  const removeAvatar = async () => {
    try { await api.put("/account/avatar", { image_data_url: null }); toast.success("Photo removed."); refresh?.(); }
    catch { toast.error("Could not remove photo."); }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    setPwError("");
    if (pw.next !== pw.confirm) { setPwError("New passwords don't match."); return; }
    setBusy(true);
    try {
      await api.post("/account/password", { current_password: pw.current, new_password: pw.next });
      toast.success("Password changed.");
      setPw({ current: "", next: "", confirm: "" });
    } catch (err) {
      const d = err.response?.data?.detail;
      setPwError(typeof d === "string" ? d : "Could not change password.");
    } finally { setBusy(false); }
  };

  const [usage, setUsage] = useState(null);
  const load = () => api.get("/account").then(({ data }) => { setData(data); setName(data.profile.name || ""); }).catch(() => toast.error("Could not load your account."));
  useEffect(() => { api.get("/usage").then(({ data }) => setUsage(data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, []);

  const saveName = async () => {
    try { await api.put("/account", { name }); toast.success("Name updated."); setEditing(false); await load(); refresh?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not update name."); }
  };
  const pauseSub = async () => {
    if (!window.confirm("Pause Premium for one billing cycle? You keep full access until the end of the period you've paid for. Then billing stops and you're on the Free plan for one cycle, after which Premium and billing resume automatically. You can resume early any time.")) return;
    setBusy(true);
    try { const { data: r } = await api.post("/account/subscription/pause"); toast.success(`Paused. Premium runs until ${new Date(r.pause_from).toLocaleDateString()}, then resumes ${new Date(r.pause_until).toLocaleDateString()}.`, { duration: 8000 }); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not pause."); } finally { setBusy(false); }
  };
  const unpauseSub = async () => {
    if (sub.pause_active) {
      const price = sub.amount ? `$${sub.amount % 1 ? sub.amount.toFixed(2) : sub.amount}` : "the current plan price";
      if (!window.confirm(`Resume Premium now? Your card will be charged ${price} today and a fresh ${sub.interval === "year" ? "year" : "month"} of Premium starts immediately.`)) return;
    }
    setBusy(true);
    try {
      const { data: r } = await api.post("/account/subscription/unpause");
      toast.success(r.charged_now ? "Premium is back — payment received and a new billing cycle starts today." : "Pause removed — Premium keeps going.", { duration: 7000 });
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not resume."); } finally { setBusy(false); }
  };
  const [cancelOpen, setCancelOpen] = useState(false);
  const cancelSub = () => setCancelOpen(true);
  const confirmCancel = async () => {
    setCancelOpen(false);
    setBusy(true);
    try { const { data: r } = await api.post("/account/subscription/cancel"); toast.success(`Premium ends on ${new Date(r.ends_at).toLocaleDateString()} — full access until then.`); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not cancel."); } finally { setBusy(false); }
  };
  const resumeSub = async () => {
    setBusy(true);
    try { await api.post("/account/subscription/resume"); toast.success("Premium will keep renewing — nothing changes."); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not resume."); } finally { setBusy(false); }
  };
  const portal = async () => {
    setBusy(true);
    try { const { data: r } = await api.post("/account/billing-portal", { origin_url: window.location.origin }); window.location.href = r.url; }
    catch (e) { toast.error(e.response?.data?.detail || "Billing portal unavailable."); setBusy(false); }
  };

  if (!data) return <div className="min-h-screen bg-slate-50"><Navbar /></div>;
  const { profile, subscription: sub } = data;
  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary">Account</p>
        <h1 className="font-heading text-4xl sm:text-5xl font-bold text-slate-900 tracking-tight mt-2" data-testid="account-title">Your account</h1>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mt-6">
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="md:col-span-7 bg-white border border-slate-200 rounded-2xl p-6" data-testid="profile-card">
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-500 mb-5"><User className="w-4 h-4" /> Profile</div>
            <div className="flex items-center gap-4 mb-6" data-testid="avatar-block">
              <div className="w-20 h-20 rounded-full overflow-hidden bg-slate-900 text-white flex items-center justify-center font-heading text-2xl border-2 border-slate-200" data-testid="account-avatar">
                {user?.picture ? <img src={user.picture} alt="" className="w-full h-full object-cover" /> : (profile.name || "?").split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase()}
              </div>
              <div className="flex flex-col gap-2">
                <label className="inline-flex">
                  <input type="file" accept="image/*" className="hidden" onChange={uploadAvatar} data-testid="avatar-file-input" />
                  <span className="cursor-pointer inline-flex items-center gap-2 text-sm font-semibold border border-slate-300 rounded-md px-3 py-2 hover:border-primary hover:text-primary" data-testid="avatar-upload-button"><Camera className="w-4 h-4" /> {user?.picture ? "Change photo" : "Add a photo"}</span>
                </label>
                {user?.picture && <button onClick={removeAvatar} className="text-xs text-slate-500 hover:text-red-600 text-left" data-testid="avatar-remove-button">Remove photo</button>}
                <p className="text-[11px] text-slate-400">Shown in the top-right corner. JPG/PNG, resized automatically.</p>
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Name</p>
                {editing ? (
                  <div className="flex items-center gap-2 mt-1">
                    <input value={name} onChange={(e) => setName(e.target.value)} maxLength={60} className="flex-1 border border-slate-300 rounded-md px-3 py-2 font-heading text-lg" data-testid="account-name-input" />
                    <Button onClick={saveName} className="bg-primary hover:bg-primary/90" data-testid="account-name-save"><Check className="w-4 h-4" /></Button>
                    <Button variant="ghost" onClick={() => { setEditing(false); setName(profile.name || ""); }}>Cancel</Button>
                  </div>
                ) : (
                  <button onClick={() => setEditing(true)} className="flex items-center gap-2 mt-1 group" data-testid="account-name-edit">
                    <span className="font-heading text-2xl text-slate-900" data-testid="account-name">{profile.name}</span>
                    {profile.role === "admin" && <Badge className="bg-slate-900 text-white hover:bg-slate-900 text-[10px] uppercase tracking-wider" data-testid="account-admin-badge">Admin</Badge>}
                    <Pencil className="w-4 h-4 text-slate-400 group-hover:text-slate-900" />
                  </button>
                )}
              </div>
              <div><p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Email</p><p className="text-slate-800 mt-1" data-testid="account-email">{profile.email}</p></div>
              <div className="flex gap-8">
                <div><p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Sign-in</p><p className="text-slate-800 mt-1 capitalize">{profile.provider === "google" ? "Google" : "Email & password"}</p></div>
                {profile.created_at && <div><p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Member since</p><p className="text-slate-800 mt-1">{new Date(profile.created_at).toLocaleDateString()}</p></div>}
              </div>
              <div className="flex items-center justify-between gap-4 pt-4 border-t border-slate-100" data-testid="appearance-row">
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Appearance</p>
                  <p className="text-slate-800 mt-1 flex items-center gap-2">{theme === "dark" ? <Moon className="w-4 h-4 text-amber-400" /> : <Sun className="w-4 h-4 text-amber-500" />} {theme === "dark" ? "Dark mode" : "Light mode"}</p>
                </div>
                <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">Dark mode <Switch checked={theme === "dark"} onCheckedChange={(v) => setTheme(v ? "dark" : "light")} data-testid="dark-mode-toggle" /></label>
              </div>
            </div>
            <Button variant="outline" onClick={async () => { await logout(); navigate("/"); }} className="mt-6" data-testid="account-logout-button"><LogOut className="w-4 h-4 mr-2" /> Log out</Button>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 }} className="md:col-span-6 md:order-3"><ReferralCard /></motion.div>
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="md:col-span-12 md:order-4"><EmailPreferences /></motion.div>
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="md:col-span-6 bg-white border border-slate-200 rounded-2xl p-6 md:order-2" data-testid="password-card">
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-500 mb-5"><KeyRound className="w-4 h-4" /> Password</div>
            {profile.provider === "google" ? (
              <p className="text-sm text-slate-500">You sign in with Google, so there's no First Step password to change.</p>
            ) : (
              <form onSubmit={changePassword} className="grid sm:grid-cols-3 gap-3">
                <input type="password" required placeholder="Current password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} className="border border-slate-300 rounded-md px-3 py-2 text-sm" data-testid="current-password-input" />
                <input type="password" required placeholder="New password (8+ chars, letter + number)" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} className="border border-slate-300 rounded-md px-3 py-2 text-sm" data-testid="new-password-input" />
                <input type="password" required placeholder="Confirm new password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} className="border border-slate-300 rounded-md px-3 py-2 text-sm" data-testid="confirm-password-input" />
                <div className="sm:col-span-3 flex items-center gap-3">
                  <Button type="submit" disabled={busy} className="bg-slate-900 hover:bg-slate-800" data-testid="change-password-button">Change password</Button>
                  {pwError && <p className="text-sm text-red-600" data-testid="password-error">{pwError}</p>}
                </div>
              </form>
            )}
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className={`md:col-span-5 rounded-2xl p-6 ${sub.is_premium ? "bg-slate-950 text-white" : "bg-white border border-slate-200"}`} data-testid="subscription-card">
            <div className={`flex items-center gap-2 text-xs font-bold uppercase tracking-wider mb-5 ${sub.is_premium ? "text-amber-400" : "text-slate-500"}`}><CreditCard className="w-4 h-4" /> Subscription</div>
            <div className="flex items-center gap-3">
              {sub.is_premium ? <Crown className="w-8 h-8 text-amber-400" /> : <User className="w-8 h-8 text-slate-400" />}
              <div>
                {user?.admin_preview && <p className="mb-2 text-[11px] font-bold uppercase tracking-wider rounded px-2 py-1 inline-block bg-amber-400 text-slate-950" data-testid="subscription-preview-note">Admin preview · showing the {user.admin_preview.tier === "pro" ? "Premium" : "Free"} view — your real membership is unchanged</p>}
                <p className="font-heading text-2xl" data-testid="subscription-plan">{sub.is_premium ? (sub.plan || "Premium").replace("Pro Candidate Pass", "Premium") : sub.pause_active ? "Premium — paused" : sub.payment_failed ? "Premium — payment issue" : "Free Recruit"}</p>
                <p className={`text-sm ${sub.is_premium ? "text-slate-400" : "text-slate-500"}`} data-testid="subscription-detail">
                  {sub.payment_failed
                    ? "Your last payment didn't go through · Premium is on hold until your card is updated and the payment succeeds"
                    : sub.pause_active
                    ? `Billing paused · Free access for now · $${sub.amount}/${sub.interval} billing and Premium resume ${new Date(sub.pause_until).toLocaleDateString()}`
                    : sub.is_premium
                    ? sub.managed_by_stripe
                      ? `$${sub.amount}/${sub.interval} · ${sub.cancel_at_period_end ? "ends" : "renews"} ${sub.renews_at ? new Date(sub.renews_at).toLocaleDateString() : "—"}`
                      : "Full access · no billing on file"
                    : "Free plan · $0"}
                </p>
                {sub.is_premium && sub.managed_by_stripe && (sub.launch_price || sub.discount) && (
                  <p className="text-xs text-amber-300 mt-1" data-testid="subscription-offer-note">
                    {sub.launch_price ? "Launch price — $15/month locked in while this subscription stays active." :
                      `Promo ${sub.discount.code || ""} applied — ${sub.discount.percent_off ? `${sub.discount.percent_off}% off` : `$${(sub.discount.amount_off / 100).toFixed(2)} off`}${sub.discount.duration === "repeating" ? ` for ${sub.discount.duration_in_months} months${sub.discount.end ? ` (through ${new Date(sub.discount.end).toLocaleDateString()})` : ""}` : " your first payment"}.`}
                  </p>
                )}
              </div>
            </div>
            {!sub.is_premium && !sub.pause_active && !sub.payment_failed && <FreeVsPremium />}
            {sub.cancel_at_period_end && <Badge className="mt-4 bg-amber-500 text-slate-950 hover:bg-amber-500">Ends at period end · Premium until then</Badge>}
                  {sub.paused && !sub.cancel_at_period_end && <Badge className="mt-4 bg-sky-400 text-slate-950 hover:bg-sky-400" data-testid="paused-badge">{sub.pause_active ? `Paused · Premium resumes ${new Date(sub.pause_until).toLocaleDateString()}` : `Pause scheduled · Premium until ${new Date(sub.pause_from).toLocaleDateString()}, back ${new Date(sub.pause_until).toLocaleDateString()}`}</Badge>}
            {sub.is_premium && usage && <div className="mt-5"><InterviewUsage usage={usage} dark showBuy={false} /></div>}
            <div className="mt-6 flex flex-col gap-2">
              {!sub.is_premium && !sub.pause_active && !sub.payment_failed && <Button onClick={() => navigate("/pricing")} className="bg-primary hover:bg-primary/90" data-testid="account-upgrade-button"><Crown className="w-4 h-4 mr-2" /> Upgrade to Premium — <MonthlyPrice className="ml-1" /></Button>}
              {sub.managed_by_stripe && (
                <>
                  <Button onClick={portal} disabled={busy} variant={sub.payment_failed ? "default" : "outline"} className={sub.payment_failed ? "bg-red-600 text-white hover:bg-red-500" : "border-slate-600 text-white hover:bg-white/10 bg-transparent"} data-testid="billing-portal-button"><ExternalLink className="w-4 h-4 mr-2" /> {sub.payment_failed ? "Update card to restore Premium" : "Manage billing & payment method"}</Button>
                  {sub.paused && !sub.cancel_at_period_end && (
                    <Button onClick={unpauseSub} disabled={busy} className="bg-amber-500 text-slate-950 hover:bg-amber-400" data-testid="unpause-subscription-button">
                      {sub.pause_active ? "Resume Premium now" : "Undo pause — keep Premium"}
                    </Button>
                  )}
                  {!sub.paused && !sub.cancel_at_period_end && !sub.payment_failed && sub.interval !== "year" && (
                    <Button onClick={pauseSub} disabled={busy} variant="outline" className="border-slate-600 text-white hover:bg-white/10 bg-transparent" data-testid="pause-subscription-button"><PauseCircle className="w-4 h-4 mr-2" /> Pause for one billing cycle</Button>
                  )}
                  {sub.interval === "year" && !sub.cancel_at_period_end && !sub.payment_failed && (
                    <p className="text-xs text-slate-400" data-testid="pause-unavailable-annual">Pausing isn't available on the yearly plan. You can cancel any time and keep Premium through the end of your paid year.</p>
                  )}
                  {sub.cancel_at_period_end
                    ? <Button onClick={resumeSub} disabled={busy} className="bg-amber-500 text-slate-950 hover:bg-amber-400" data-testid="resume-subscription-button">Undo — keep Premium</Button>
                    : <Button onClick={cancelSub} disabled={busy} variant="ghost" className="text-slate-400 hover:text-white hover:bg-white/10" data-testid="cancel-subscription-button">Cancel anytime</Button>}
                  <CancelPremiumDialog open={cancelOpen} onClose={() => setCancelOpen(false)} canPause={!sub.paused && sub.interval !== "year"} onPause={() => { setCancelOpen(false); pauseSub(); }} onCancel={confirmCancel} periodEnd={sub.renews_at} />
                </>
              )}
              {sub.is_premium && !sub.managed_by_stripe && <p className="text-xs text-slate-500">This Premium access isn't tied to a Stripe subscription, so there's nothing to cancel or change here.</p>}
            </div>
          </motion.div>
        </div>

      </div>
    </div>
  );
}
